import AsyncStorage from '@react-native-async-storage/async-storage';
import { PrivateWaypoint, WaypointCategory } from '../types';

const CACHE_PREFIX = 'waypoint_cache_';
const INDEX_KEY = 'waypoint_index';
const CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes
const MAX_CACHE_SIZE = 100; // Maximum waypoints to keep in memory

interface CacheEntry {
  data: PrivateWaypoint[];
  timestamp: number;
  query: string;
}

interface IndexEntry {
  key: string;
  timestamp: number;
  size: number;
}

class WaypointCacheManager {
  private memoryCache: Map<string, CacheEntry> = new Map();
  private cacheIndex: IndexEntry[] = [];

  private getCacheKey(searchTerm: string, category: WaypointCategory | 'all', page: number): string {
    return `${CACHE_PREFIX}${searchTerm}_${category}_${page}`;
  }

  async get(searchTerm: string, category: WaypointCategory | 'all', page: number): Promise<PrivateWaypoint[] | null> {
    const key = this.getCacheKey(searchTerm, category, page);
    
    // Check memory cache first
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && Date.now() - memoryEntry.timestamp < CACHE_EXPIRY) {
      return memoryEntry.data;
    }
    
    // Check persistent cache
    try {
      const cached = await AsyncStorage.getItem(key);
      if (cached) {
        const entry: CacheEntry = JSON.parse(cached);
        if (Date.now() - entry.timestamp < CACHE_EXPIRY) {
          // Add to memory cache for faster subsequent access
          if (this.memoryCache.size < MAX_CACHE_SIZE) {
            this.memoryCache.set(key, entry);
          }
          return entry.data;
        }
      }
    } catch (error) {
      console.error('Cache read error:', error);
    }
    
    return null;
  }

  async set(searchTerm: string, category: WaypointCategory | 'all', page: number, data: PrivateWaypoint[]): Promise<void> {
    const key = this.getCacheKey(searchTerm, category, page);
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      query: `${searchTerm}_${category}_${page}`
    };
    
    // Add to memory cache
    if (this.memoryCache.size >= MAX_CACHE_SIZE) {
      // Remove oldest entry
      const firstKey = this.memoryCache.keys().next().value;
      if (firstKey) {
        this.memoryCache.delete(firstKey);
      }
    }
    this.memoryCache.set(key, entry);
    
    // Save to persistent cache in background without blocking
    setTimeout(async () => {
      try {
        await AsyncStorage.setItem(key, JSON.stringify(entry));
        await this.updateIndex(key, entry.data.length);
      } catch (error) {
        console.error('Cache write error:', error);
      }
    }, 0);
  }

  private async updateIndex(key: string, size: number): Promise<void> {
    try {
      const indexData = await AsyncStorage.getItem(INDEX_KEY);
      let index: IndexEntry[] = indexData ? JSON.parse(indexData) : [];
      
      // Remove existing entry if present
      index = index.filter(entry => entry.key !== key);
      
      // Add new entry
      index.push({
        key,
        timestamp: Date.now(),
        size
      });
      
      // Keep only recent entries (last 50)
      if (index.length > 50) {
        index = index.slice(-50);
      }
      
      await AsyncStorage.setItem(INDEX_KEY, JSON.stringify(index));
      this.cacheIndex = index;
    } catch (error) {
      console.error('Index update error:', error);
    }
  }

  async clearCache(): Promise<void> {
    this.memoryCache.clear();
    
    try {
      // Load index to get all cache keys
      const indexData = await AsyncStorage.getItem(INDEX_KEY);
      if (indexData) {
        const index: IndexEntry[] = JSON.parse(indexData);
        
        // Remove all cache entries
        const removePromises = index.map(entry => AsyncStorage.removeItem(entry.key));
        await Promise.all(removePromises);
        
        // Clear the index
        await AsyncStorage.removeItem(INDEX_KEY);
      }
    } catch (error) {
      console.error('Cache clear error:', error);
    }
  }

  async cleanOldEntries(): Promise<void> {
    // This is now only called manually, not automatically
    try {
      const indexData = await AsyncStorage.getItem(INDEX_KEY);
      if (!indexData) return;
      
      const index: IndexEntry[] = JSON.parse(indexData);
      const now = Date.now();
      const validEntries: IndexEntry[] = [];
      const removePromises: Promise<void>[] = [];
      
      for (const entry of index) {
        if (now - entry.timestamp > CACHE_EXPIRY) {
          removePromises.push(AsyncStorage.removeItem(entry.key));
        } else {
          validEntries.push(entry);
        }
      }
      
      if (removePromises.length > 0) {
        await Promise.all(removePromises);
        await AsyncStorage.setItem(INDEX_KEY, JSON.stringify(validEntries));
        this.cacheIndex = validEntries;
      }
    } catch (error) {
      console.error('Cache cleanup error:', error);
    }
  }
}

export const waypointCache = new WaypointCacheManager();